/**
 * WarehouseLagerplaetze — Digital Twin Cockpit (v2.102.1)
 * Complete rewrite: Progress bar, 3 KPI cards, search, 3 tabs.
 * READ-ONLY — all data from Odoo via backend.
 *
 * @page /warehouse/lagerplaetze
 * @roles admin, manager
 * @api GET /api/warehouse/lagerplaetze/stats
 * @api GET /api/warehouse/lagerplaetze/offene-regale
 * @api GET /api/warehouse/lagerplaetze/regale
 * @api GET /api/warehouse/lagerplaetze/kunden
 * @api GET /api/warehouse/lagerplaetze/suche?q=
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useTabParam from '../hooks/useTabParam';
import { Search, Check, ChevronLeft, ChevronRight, MapPin, Package, Users, CheckCircle, AlertTriangle } from 'lucide-react';
import { PageHeader } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';

const API = import.meta.env.VITE_API || '';

async function apiFetch(url) {
  const res = await fetch(`${API}${url}`, { credentials: 'include' });
  return res.json();
}

const PAGE_SIZE = 20;


// ═══════════════════════════════════════════════════════════════
// TAB: Übersicht — Offene Regale (nicht auf Map), sortiert nach Locations DESC
// ═══════════════════════════════════════════════════════════════
function UebersichtTab({ t, navigate }) {
  const [offene, setOffene] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');

  useEffect(() => {
    apiFetch('/api/warehouse/lagerplaetze/offene-regale').then(data => {
      if (Array.isArray(data)) setOffene(data);
      setLoading(false);
    });
  }, []);

  const filtered = useMemo(() => {
    if (!search) return offene;
    const q = search.toLowerCase();
    return offene.filter(r =>
      r.regal_gruppe.toLowerCase().includes(q) ||
      r.typ.toLowerCase().includes(q) ||
      (r.kunden || []).some(k => k.kunde_name.toLowerCase().includes(q))
    );
  }, [offene, search]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section header */}
      <div>
        <h3 className="text-base font-semibold" style={{ color: '#1A1A2E' }}>
          {t('warehouse.offene_regale_titel')}
        </h3>
        <p className="text-xs mt-0.5" style={{ color: '#9CA3AF' }}>
          {t('warehouse.sortiert_locations')}
        </p>
      </div>

      {/* Table */}
      <div className="border rounded-xl overflow-hidden" style={{ borderColor: '#E5E7EB', backgroundColor: 'var(--bg-card, #fff)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
              <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>#</th>
              <th className="text-left px-3 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{t('warehouse.tab_regale')}</th>
              <th className="text-left px-3 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{t('warehouse.typ') || 'Typ'}</th>
              <th className="text-right px-3 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Locations</th>
              <th className="text-right px-3 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}></th>
            </tr>
          </thead>
          <tbody>
            {paged.map((r, idx) => (
              <tr key={r.regal_gruppe} className="hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors"
                style={{ borderBottom: '1px solid #F3F4F6' }}>
                <td className="px-4 py-3 tabular-nums" style={{ color: '#9CA3AF' }}>{page * PAGE_SIZE + idx + 1}</td>
                <td className="px-3 py-3 font-mono font-medium" style={{ color: '#1A1A2E' }}>{r.regal_gruppe}</td>
                <td className="px-3 py-3" style={{ color: '#6B7280' }}>{r.typ}</td>
                <td className="px-3 py-3 text-right font-medium tabular-nums" style={{ color: '#1A1A2E' }}>{r.locations.toLocaleString('de-DE')}</td>
                <td className="px-3 py-3 text-right">
                  <button
                    onClick={() => navigate('/warehouse/einrichten')}
                    className="text-xs font-medium hover:underline"
                    style={{ color: '#338edc' }}
                  >
                    {t('warehouse.einrichten_link')} →
                  </button>
                </td>
              </tr>
            ))}
            {paged.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>
                  {t('warehouse.keine_ergebnisse')}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: '1px solid #E5E7EB' }}>
            <span className="text-xs tabular-nums" style={{ color: '#9CA3AF' }}>
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} / {filtered.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
                className="p-1.5 rounded-lg border disabled:opacity-30 hover:bg-[var(--bg-card-hover,#FAFAFE)]"
                style={{ borderColor: '#E5E7EB' }}
              >
                <ChevronLeft className="w-3.5 h-3.5" style={{ color: '#6B7280' }} />
              </button>
              <button
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
                className="p-1.5 rounded-lg border disabled:opacity-30 hover:bg-[var(--bg-card-hover,#FAFAFE)]"
                style={{ borderColor: '#E5E7EB' }}
              >
                <ChevronRight className="w-3.5 h-3.5" style={{ color: '#6B7280' }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// TAB: Regale — Alle Regale mit Typ- und Status-Filter
// ═══════════════════════════════════════════════════════════════
function RegaleTab({ t, navigate }) {
  const [regale, setRegale] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTyp, setFilterTyp] = useState('');
  const [filterStatus, setFilterStatus] = useState(''); // '', 'true', 'false'
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    setLoading(true);
    let url = '/api/warehouse/lagerplaetze/regale?warehouse_id=1';
    if (filterTyp) url += `&typ=${encodeURIComponent(filterTyp)}`;
    if (filterStatus) url += `&auf_map=${filterStatus}`;
    apiFetch(url).then(data => {
      if (Array.isArray(data)) setRegale(data);
      setLoading(false);
      setPage(0);
    });
  }, [filterTyp, filterStatus]);

  const filtered = useMemo(() => {
    if (!search) return regale;
    const q = search.toLowerCase();
    return regale.filter(r => r.regal_gruppe.toLowerCase().includes(q));
  }, [regale, search]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const typen = [...new Set(regale.map(r => r.typ))].sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9CA3AF' }} />
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(0); }}
            placeholder={t('warehouse.suche_placeholder')}
            className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-[#338edc]"
            style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}
          />
        </div>
        <select value={filterTyp} onChange={e => setFilterTyp(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none"
          style={{ borderColor: '#E5E7EB', color: '#1A1A2E', backgroundColor: 'var(--bg-input, #fff)' }}>
          <option value="">{t('warehouse.alleTypen')}</option>
          {typen.map(tp => <option key={tp} value={tp}>{tp}</option>)}
        </select>
        <div className="inline-flex p-[3px] rounded-[10px] border" style={{ backgroundColor: '#F8F9FC', borderColor: '#E5E7EB' }}>
          {[['', t('warehouse.alleTypen').replace('Types', 'All').replace('Typen', 'Alle')],
            ['true', t('warehouse.auf_map_ja')],
            ['false', t('warehouse.noch_offen')]
          ].map(([val, label]) => (
            <button key={val} onClick={() => { setFilterStatus(val); setPage(0); }}
              className={`px-3 py-1.5 text-xs font-medium rounded-[8px] transition-all ${filterStatus === val ? 'bg-white shadow-sm' : ''}`}
              style={{ color: filterStatus === val ? '#1A1A2E' : '#6B7280' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-xl overflow-hidden" style={{ borderColor: '#E5E7EB', backgroundColor: 'var(--bg-card, #fff)' }}>
        <div className="max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0" style={{ backgroundColor: 'var(--bg-card, #fff)' }}>
              <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{t('warehouse.tab_regale')}</th>
                <th className="text-left px-3 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{t('warehouse.typ') || 'Typ'}</th>
                <th className="text-right px-3 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Locations</th>
                <th className="text-center px-3 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{t('warehouse.auf_map_ja')}</th>
                <th className="text-right px-3 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}></th>
              </tr>
            </thead>
            <tbody>
              {paged.map(r => (
                <tr key={r.regal_gruppe} className="hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors"
                  style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td className="px-4 py-3 font-mono font-medium" style={{ color: '#1A1A2E' }}>{r.regal_gruppe}</td>
                  <td className="px-3 py-3" style={{ color: '#6B7280' }}>{r.typ}</td>
                  <td className="px-3 py-3 text-right font-medium tabular-nums" style={{ color: '#1A1A2E' }}>{r.locations.toLocaleString('de-DE')}</td>
                  <td className="px-3 py-3 text-center">
                    {r.auf_map
                      ? <Check className="w-4 h-4 mx-auto" style={{ color: '#10B981' }} />
                      : <span style={{ color: '#D1D5DB' }}>—</span>}
                  </td>
                  <td className="px-3 py-3 text-right">
                    {!r.auf_map && (
                      <button onClick={() => navigate('/warehouse/einrichten')}
                        className="text-xs font-medium hover:underline" style={{ color: '#338edc' }}>
                        {t('warehouse.einrichten_link')} →
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3" style={{ borderTop: '1px solid #E5E7EB' }}>
            <span className="text-xs tabular-nums" style={{ color: '#9CA3AF' }}>
              {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} / {filtered.length}
            </span>
            <div className="flex items-center gap-1">
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)}
                className="p-1.5 rounded-lg border disabled:opacity-30 hover:bg-[var(--bg-card-hover,#FAFAFE)]"
                style={{ borderColor: '#E5E7EB' }}>
                <ChevronLeft className="w-3.5 h-3.5" style={{ color: '#6B7280' }} />
              </button>
              <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}
                className="p-1.5 rounded-lg border disabled:opacity-30 hover:bg-[var(--bg-card-hover,#FAFAFE)]"
                style={{ borderColor: '#E5E7EB' }}>
                <ChevronRight className="w-3.5 h-3.5" style={{ color: '#6B7280' }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// TAB: Kunden — Kunden-Zuordnung aus Pick-Daten
// ═══════════════════════════════════════════════════════════════
function KundenTab({ t }) {
  const [kunden, setKunden] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/api/warehouse/lagerplaetze/kunden?tage=30').then(data => {
      if (Array.isArray(data)) setKunden(data);
      setLoading(false);
    });
  }, []);

  // Total locations for percentage
  const totalLocations = useMemo(() =>
    kunden.reduce((s, k) => s + k.locations, 0)
  , [kunden]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-5 h-5 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="border rounded-xl overflow-hidden" style={{ borderColor: '#E5E7EB', backgroundColor: 'var(--bg-card, #fff)' }}>
        <div className="max-h-[500px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0" style={{ backgroundColor: 'var(--bg-card, #fff)' }}>
              <tr style={{ borderBottom: '1px solid #E5E7EB' }}>
                <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{t('warehouse.tab_kunden')}</th>
                <th className="text-right px-3 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>{t('warehouse.tab_regale')}</th>
                <th className="text-right px-3 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Locations</th>
                <th className="text-right px-3 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Picks</th>
                <th className="text-right px-3 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#9CA3AF' }}>%</th>
              </tr>
            </thead>
            <tbody>
              {kunden.map(k => (
                <tr key={k.kunde_id} className="hover:bg-[var(--bg-card-hover,#FAFAFE)] transition-colors"
                  style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td className="px-4 py-3 font-medium" style={{ color: '#1A1A2E' }}>{k.kunde_name}</td>
                  <td className="px-3 py-3 text-right tabular-nums" style={{ color: '#1A1A2E' }}>{k.regale}</td>
                  <td className="px-3 py-3 text-right tabular-nums" style={{ color: '#1A1A2E' }}>{k.locations.toLocaleString('de-DE')}</td>
                  <td className="px-3 py-3 text-right tabular-nums" style={{ color: '#6B7280' }}>{k.picks.toLocaleString('de-DE')}</td>
                  <td className="px-3 py-3 text-right tabular-nums" style={{ color: '#6B7280' }}>
                    {totalLocations > 0 ? ((k.locations / totalLocations) * 100).toFixed(1) : 0}%
                  </td>
                </tr>
              ))}
              {kunden.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-sm" style={{ color: '#9CA3AF' }}>
                    {t('warehouse.keine_ergebnisse')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT — Digital Twin Cockpit
// ═══════════════════════════════════════════════════════════════
export default function WarehouseLagerplaetze() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useTabParam('uebersicht');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  // Load stats
  useEffect(() => {
    apiFetch('/api/warehouse/lagerplaetze/stats').then(data => {
      if (data && !data.error) setStats(data);
      setLoading(false);
    });
  }, []);

  // Global search with debounce
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults(null);
      return;
    }
    const timer = setTimeout(() => {
      setSearchLoading(true);
      apiFetch(`/api/warehouse/lagerplaetze/suche?q=${encodeURIComponent(searchQuery)}`).then(data => {
        if (data && !data.error) setSearchResults(data);
        setSearchLoading(false);
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const s = stats || { total_lagerplaetze: 0, auf_map: 0, nicht_auf_map: 0, fortschritt_pct: 0, aktive_kunden: 0 };
  const visualisiert = s.auf_map || 0;
  const offen = s.nicht_auf_map || 0;
  const total = s.total_lagerplaetze || 0;
  const pct = s.fortschritt_pct || 0;
  const kundenCount = s.aktive_kunden || 0;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title={t('warehouse.digital_twin_fortschritt')}
        subtitle={t('warehouse.fortschritt')}
      />

      {/* ── Progress Bar ── */}
      <div className="border rounded-xl p-6" style={{ borderColor: '#E5E7EB', backgroundColor: 'var(--bg-card, #fff)' }}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium" style={{ color: '#6B7280' }}>
            {t('warehouse.fortschritt')}
          </span>
          <span className="text-sm font-semibold" style={{ color: '#1A1A2E' }}>
            {visualisiert.toLocaleString('de-DE')} / {total.toLocaleString('de-DE')} Locations ({pct}%)
          </span>
        </div>
        <div className="w-full h-3 rounded-full" style={{ backgroundColor: '#F8F9FC' }}>
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.max(pct, 1)}%`,
              backgroundColor: '#338edc'
            }}
          />
        </div>
      </div>

      {/* ── 3 KPI Cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Card 1: Visualisiert */}
        <div className="border rounded-xl p-5" style={{ borderColor: '#E5E7EB', backgroundColor: 'var(--bg-card, #fff)' }}>
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={16} className="text-[#338edc]" />
            <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
              {t('warehouse.visualisiert')}
            </div>
          </div>
          <div className="text-3xl font-bold mb-1" style={{ color: '#1A1A2E' }}>
            {visualisiert.toLocaleString('de-DE')}
          </div>
          <div className="text-sm mb-3" style={{ color: '#9CA3AF' }}>Locations</div>
          <button
            onClick={() => navigate('/warehouse/einrichten')}
            className="text-sm font-medium hover:underline"
            style={{ color: '#338edc' }}
          >
            {t('warehouse.auf_map')} →
          </button>
        </div>

        {/* Card 2: Noch offen */}
        <div className="border rounded-xl p-5" style={{ borderColor: '#E5E7EB', backgroundColor: 'var(--bg-card, #fff)' }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={16} className="text-[#F59E0B]" />
            <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
              {t('warehouse.noch_offen')}
            </div>
          </div>
          <div className="text-3xl font-bold mb-1" style={{ color: '#1A1A2E' }}>
            {offen.toLocaleString('de-DE')}
          </div>
          <div className="text-sm mb-3" style={{ color: '#9CA3AF' }}>Locations</div>
          <button
            onClick={() => navigate('/warehouse/einrichten')}
            className="text-sm font-medium hover:underline"
            style={{ color: '#338edc' }}
          >
            {t('warehouse.jetzt_einrichten')} →
          </button>
        </div>

        {/* Card 3: Kunden */}
        <div className="border rounded-xl p-5" style={{ borderColor: '#E5E7EB', backgroundColor: 'var(--bg-card, #fff)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} className="text-[#8B5CF6]" />
            <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
              {t('warehouse.kunden_zugeordnet')}
            </div>
          </div>
          <div className="text-3xl font-bold mb-1" style={{ color: '#1A1A2E' }}>
            {kundenCount}
          </div>
          <div className="text-sm" style={{ color: '#9CA3AF' }}>
            {t('warehouse.kunden_zugeordnet')}
          </div>
        </div>
      </div>

      {/* ── Search ── */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: '#9CA3AF' }} />
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={t('warehouse.suche_placeholder')}
          className="w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:border-[#338edc]"
          style={{ borderColor: '#E5E7EB', color: '#1A1A2E', backgroundColor: 'var(--bg-input, #fff)' }}
        />
      </div>

      {/* Search Results */}
      {searchResults && searchQuery.length >= 2 && (
        <div className="border rounded-xl overflow-hidden -mt-3" style={{ borderColor: '#E5E7EB', backgroundColor: 'var(--bg-card, #fff)' }}>
          {searchLoading ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-4 h-4 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div>
              {/* Regale */}
              {searchResults.regale?.length > 0 && (
                <div className="px-4 py-3" style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#9CA3AF' }}>
                    {t('warehouse.tab_regale')} ({searchResults.regale.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {searchResults.regale.map(r => (
                      <span key={r.regal_gruppe} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border rounded-lg text-xs"
                        style={{ borderColor: '#E5E7EB' }}>
                        <span className="font-mono font-medium" style={{ color: '#1A1A2E' }}>{r.regal_gruppe}</span>
                        <span style={{ color: '#9CA3AF' }}>{r.typ}</span>
                        <span style={{ color: '#6B7280' }}>{r.locations} Loc</span>
                        {r.auf_map && <Check className="w-3 h-3" style={{ color: '#10B981' }} />}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* Kunden */}
              {searchResults.kunden?.length > 0 && (
                <div className="px-4 py-3" style={{ borderBottom: '1px solid #E5E7EB' }}>
                  <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#9CA3AF' }}>
                    {t('warehouse.tab_kunden')} ({searchResults.kunden.length})
                  </div>
                  {searchResults.kunden.map(k => (
                    <div key={k.kunde_id} className="flex items-center justify-between py-1 text-xs">
                      <span className="font-medium" style={{ color: '#1A1A2E' }}>{k.kunde_name}</span>
                      <span style={{ color: '#6B7280' }}>{k.regale} {t('warehouse.tab_regale')} · {k.locations} Loc</span>
                    </div>
                  ))}
                </div>
              )}
              {/* Locations */}
              {searchResults.locations?.length > 0 && (
                <div className="px-4 py-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: '#9CA3AF' }}>
                    Locations ({searchResults.locations.length})
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {searchResults.locations.map(l => (
                      <span key={l.location_name} className="px-2.5 py-1 border rounded-lg text-xs font-mono"
                        style={{ borderColor: '#E5E7EB', color: '#1A1A2E' }}>
                        {l.location_name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* No results */}
              {(searchResults.regale?.length || 0) + (searchResults.kunden?.length || 0) + (searchResults.locations?.length || 0) === 0 && (
                <div className="px-4 py-6 text-center text-sm" style={{ color: '#9CA3AF' }}>
                  {t('warehouse.keine_ergebnisse')}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Tab Bar (Earney-Style) ── */}
      <div className="inline-flex p-[3px] rounded-[10px] border" style={{ backgroundColor: '#F8F9FC', borderColor: '#E5E7EB' }}>
        {['uebersicht', 'regale', 'kunden'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium rounded-[8px] transition-all ${
              activeTab === tab ? 'bg-white shadow-sm' : ''
            }`}
            style={{ color: activeTab === tab ? '#1A1A2E' : '#6B7280' }}
          >
            {t(`warehouse.tab_${tab}`)}
          </button>
        ))}
      </div>

      {/* ── Tab Content ── */}
      {activeTab === 'uebersicht' && <UebersichtTab t={t} navigate={navigate} />}
      {activeTab === 'regale' && <RegaleTab t={t} navigate={navigate} />}
      {activeTab === 'kunden' && <KundenTab t={t} />}
    </div>
  );
}
